/**
 * Join-flow onboarding API — the backend contracts the join wizard consumes.
 *
 *   GET  /api/orgs/:orgId/join-onboarding/  → join onboarding status for the org
 *
 * Plain React Query hooks over `apiGet` (FRONTEND_PATTERNS § Pattern B).
 */
import { useMutation, useQuery } from '@tanstack/react-query';

import { useAuthInfo } from '@/allauth_auth/hooks';
import { apiGet, apiPost } from '@/lib/http/client';

export function joinOnboardingStatusPath(orgId: string): string {
  return `/api/orgs/${orgId}/join-onboarding/`;
}

export function joinOnboardingCompletePath(orgId: string): string {
  return `/api/orgs/${orgId}/join-onboarding/complete/`;
}

export interface JoinOnboardingStatus {
  required: boolean;
  completed: boolean;
  org_name: string;
  has_mailbox_account: boolean;
  /**
   * Prefill for "Tell us about you", read from the user record. Empty when the
   * admin invited by email alone — which is exactly why the step asks.
   */
  first_name: string;
  last_name: string;
  // No `timezone` here on purpose. The canonical source is the user's profile
  // (useUserProfileData), so the wizard reads it from there rather than having a
  // second copy echoed through this endpoint that could drift from it.
}

export interface JoinOnboardingCompletePayload {
  /**
   * Omitted or empty leaves the stored value untouched rather than blanking it —
   * the invitee may clear a prefilled field, and that must not wipe a name the
   * admin set.
   */
  first_name?: string;
  last_name?: string;
  role?: string;
  /** IANA zone name. The server rejects anything `zoneinfo` does not know. */
  timezone?: string;
  mailbox_visibility: 'full' | 'exclude' | null;
  filtered_emails: string[];
  filtered_domains: string[];
}

// The completion endpoint returns the PERSISTED join-onboarding status (the same
// shape as the GET status endpoint), not a bespoke {completed, org_id} object:
// complete() is a no-op for a membership that is not pending, so the server
// echoes back the real persisted status rather than an unconditional success.
export type JoinOnboardingCompleteResponse = JoinOnboardingStatus;

/**
 * Cache key for the join-onboarding status.
 *
 * Scoped to (org, USER), not org alone. The status is a per-user gating decision held at
 * `staleTime: Infinity`, and logging out does not reload the SPA — so an org-only key lets the
 * next user in the same tab read the previous user's cached answer and be wrongly waved past the
 * wizard (or wrongly redirected into it). Clearing the cache on logout does not cover this on its
 * own: `allauth.auth.change` fires on ANY 401/410 (a session expiry, a failed reauth), and that
 * path runs through `AuthChangeRedirector`, not `Logout.tsx`. Keying by user makes the stale read
 * impossible rather than relying on every present and future sign-out path to remember to clear.
 *
 * `(user, org)`-scoped client state follows this same shape elsewhere — see
 * `useDashboardSectionPref` / `dashboardSectionPrefs`.
 */
export function joinOnboardingStatusKey(
  orgId: string,
  userId: number | undefined
) {
  return ['join-onboarding', 'status', orgId, userId] as const;
}

/** Join onboarding gate status. Immutable for the life of the session. */
export function useJoinOnboardingStatus(orgId: string) {
  const { user } = useAuthInfo();
  const userId: number | undefined = user?.id;
  return useQuery({
    queryKey: joinOnboardingStatusKey(orgId, userId),
    queryFn: () =>
      apiGet<JoinOnboardingStatus>(joinOnboardingStatusPath(orgId)),
    // Callers pass `orgId ?? ''`; never fire a request for an empty id — it
    // would hit the malformed `/api/orgs//join-onboarding/` path.
    //
    // Deliberately NOT also gated on `userId !== undefined`. Every route that
    // mounts this hook sits behind `AuthenticatedRoute`, so a rendered consumer
    // always has a resolved user; and reading a `userId: undefined` entry would
    // itself require an unauthenticated viewer, who cannot reach these routes.
    // The cross-user leak this key closes needs two AUTHENTICATED users sharing
    // one entry, which distinct ids already prevent.
    enabled: orgId !== '',
    staleTime: Infinity,
    retry: false,
  });
}

export function useCompleteJoinOnboarding(orgId: string) {
  return useMutation({
    mutationFn: (payload: JoinOnboardingCompletePayload) =>
      apiPost<JoinOnboardingCompleteResponse>(
        joinOnboardingCompletePath(orgId),
        payload
      ),
  });
}
